import { normalizeName } from '../funnel/normalize';
import { LIMA_DISTRICTS } from '../schemas/contacts';
import { applyMenu, understandingFromChoice } from './guided';
import {
  BOT_INTENT_LABELS,
  type BotConversationState,
  type BotExtracted,
  type BotIntent,
  type BotTemperature,
  type BotUnderstanding,
} from './types';

/**
 * Entendimiento por reglas (palabras clave, números, opciones). Lo usa el proveedor `fake`, el plan Básico y sirve
 * de respaldo cuando el modelo no devuelve un JSON válido. Trabaja sobre el texto normalizado (mayúsculas, sin tildes).
 */
const has = (t: string, ...words: string[]) => words.some((w) => t.includes(normalizeName(w)));

const ACCENTS: Record<string, string> = { Á: 'A', É: 'E', Í: 'I', Ó: 'O', Ú: 'U', Ü: 'U', Ñ: 'N' };
/** Mayúsculas sin tildes conservando la longitud y la puntuación (los índices coinciden con el texto original). */
export function lite(text: string): string {
  return text.toUpperCase().replace(/[ÁÉÍÓÚÜÑ]/g, (c) => ACCENTS[c] ?? c);
}

const WORD_NUMBERS: Record<string, number> = {
  UN: 1,
  UNA: 1,
  UNO: 1,
  DOS: 2,
  TRES: 3,
  CUATRO: 4,
  CINCO: 5,
};

function parseAmount(raw: string, suffix: string | undefined): number | null {
  let s = raw.replace(/\s+/g, '');
  if (/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(s)) s = s.replace(/,/g, '');
  else if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) s = s.replace(/\./g, '').replace(',', '.');
  else if (/^\d+,\d+$/.test(s)) s = s.replace(',', '.');
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return suffix ? n * 1000 : n;
}

/** Datos que se pueden leer del mensaje (presupuesto, dormitorios, distrito, para qué, forma de pago, cuándo, horario). */
export function extractData(text: string): BotExtracted {
  const norm = normalizeName(text);
  const lt = lite(text);
  const data: BotExtracted = {};

  // Presupuesto: "150 mil", "150k", "S/ 350,000", "350 000", "$120000". Se ignoran números tipo teléfono.
  const money =
    /(?:S\/\.?|US\$|USD|\$|SOLES|DOLARES)?\s*(\d{1,3}(?:[.,]\d{3})+|\d{1,3}(?: \d{3})+|\d{2,7})\s*(MIL|K)?\b/g;
  let m: RegExpExecArray | null;
  while ((m = money.exec(lt))) {
    const n = parseAmount(m[1]!.replace(/ /g, ','), m[2]);
    if (n === null) continue;
    if (/^9\d{8}$/.test(m[1]!.replace(/\D/g, ''))) continue;
    if (n >= 10_000 && n <= 5_000_000) {
      data.budget = n;
      const around = lt.slice(Math.max(0, m.index - 12), m.index + m[0].length + 12);
      data.budgetCurrency = /S\/|SOLES/.test(around) ? 'PEN' : 'USD';
      break;
    }
  }

  // Dormitorios: "2 dormitorios", "tres habitaciones", "1 dorm", "estudio".
  const bed =
    /(\d|UN|UNA|UNO|DOS|TRES|CUATRO|CINCO)\s*(DORMITORIOS?|DORMS?|HABITACION(?:ES)?|CUARTOS?|HAB\b|AMBIENTES?)/.exec(
      lt,
    );
  if (bed) {
    const raw = bed[1]!;
    data.bedrooms = /^\d$/.test(raw) ? Number(raw) : (WORD_NUMBERS[raw] ?? null);
  } else if (has(norm, 'estudio', 'monoambiente')) data.bedrooms = 0;

  // Distrito: el nombre más largo que aparezca.
  let best: string | null = null;
  for (const d of LIMA_DISTRICTS) {
    const nd = normalizeName(d);
    if (norm.includes(nd) && (!best || nd.length > normalizeName(best).length)) best = d;
  }
  if (best) data.district = best;

  if (has(norm, 'para vivir', 'vivienda', 'mudar', 'mi familia', 'para mi', 'primera vivienda'))
    data.purpose = 'VIVIENDA';
  else if (has(norm, 'invertir', 'inversion', 'alquilar', 'renta', 'para rentar'))
    data.purpose = 'INVERSION';

  if (has(norm, 'al contado', 'contado', 'de contado')) data.financing = 'CONTADO';
  else if (has(norm, 'credito directo', 'financiamiento directo', 'directo con ustedes'))
    data.financing = 'CREDITO_DIRECTO';
  else if (
    has(
      norm,
      'hipotec',
      'banco',
      'bcp',
      'bbva',
      'interbank',
      'scotiabank',
      'mivivienda',
      'techo propio',
      'bono',
      'credito',
    )
  )
    data.financing = 'HIPOTECARIO';

  if (
    has(
      norm,
      'urgente',
      'este mes',
      'lo antes posible',
      'inmediato',
      'de inmediato',
      'ya mismo',
      'cuanto antes',
    )
  )
    data.horizon = 'INMEDIATO';
  else if (
    has(norm, 'proximo mes', 'proximos meses', '2 meses', '3 meses', 'pronto', 'en unos meses')
  )
    data.horizon = 'MENOS_3_MESES';
  else if (has(norm, '6 meses', 'medio ano', 'seis meses')) data.horizon = 'DE_3_A_6_MESES';
  else if (has(norm, 'fin de ano', 'un ano', '12 meses', 'proximo ano', 'el otro ano'))
    data.horizon = 'DE_6_A_12_MESES';
  else if (
    has(norm, 'mas adelante', 'todavia no', 'solo informacion', 'solo estoy viendo', 'por ahora no')
  )
    data.horizon = 'MAS_12_MESES';

  const when =
    /\b(HOY|MANANA|PASADO MANANA|LUNES|MARTES|MIERCOLES|JUEVES|VIERNES|SABADO|DOMINGO)\b[^.,;!?]{0,40}/.exec(
      lt,
    ) ??
    /\b(\d{1,2})(?::\d{2})?\s*(AM|PM|A\.?\s?M\.?|P\.?\s?M\.?|DE LA TARDE|DE LA MANANA|HRS|H)\b/.exec(
      lt,
    ) ??
    /\bA LAS \d{1,2}(:\d{2})?\b/.exec(lt);
  if (when)
    data.appointmentText = text
      .slice(when.index, when.index + when[0].length)
      .trim()
      .slice(0, 120);

  if (has(norm, 'virtual', 'zoom', 'meet', 'videollamada', 'por llamada', 'en linea', 'online'))
    data.appointmentMode = 'VIRTUAL';
  else if (has(norm, 'sala de ventas', 'presencial', 'ir a ver', 'visitar', 'caseta', 'en persona'))
    data.appointmentMode = 'PRESENCIAL';

  // Nombre: hasta dos palabras, sin arrastrar conectores ("me llamo Rosa y busco…" → "Rosa").
  const name =
    /(?:me llamo|mi nombre es|soy)\s+([A-ZÁÉÍÓÚÑa-záéíóúñ]{2,})(?:\s+(?!(?:y|e|o|u|que|de|del|la|el|en|un|una|con|para|por|pero)\b)([A-ZÁÉÍÓÚÑa-záéíóúñ]{2,}))?/i.exec(
      text,
    );
  if (name) data.name = [name[1], name[2]].filter(Boolean).join(' ').slice(0, 60);
  return data;
}

/** Intención del mensaje; el orden importa (primero lo que corta la conversación). */
export function classifyIntent(
  text: string,
  state: BotConversationState,
  data: BotExtracted,
): BotIntent {
  const norm = normalizeName(text);
  const short = norm.split(/\s+/).filter(Boolean).length <= 6;
  if (
    has(
      norm,
      'no me escrib',
      'no escribas',
      'no escriban',
      'dejen de',
      'deja de escribir',
      'basta',
      'spam',
      'no quiero mensajes',
      'bloque',
      'borra mi numero',
      'eliminen mi',
      'no me molest',
    )
  )
    return 'STOP';
  if (
    has(
      norm,
      'persona',
      'humano',
      'asesor',
      'alguien real',
      'hablar con',
      'llamenme',
      'llamame',
      'que me llame',
      'un vendedor',
    )
  )
    return 'PIDE_HUMANO';
  if (
    has(
      norm,
      'no me interesa',
      'no gracias',
      'ya compre',
      'no estoy interesad',
      'no busco',
      'numero equivocado',
      'no es para mi',
      'ya no',
    )
  )
    return 'NO_INTERESA';
  if (state.stage === 'PROPONIENDO_CITA' && state.proposedSlots.length) {
    if (
      has(
        norm,
        'no puedo',
        'tampoco',
        'otro dia',
        'otro horario',
        'no me viene',
        'otra hora',
        'no me acomoda',
        'ninguno',
        'imposible',
      )
    )
      return data.appointmentText ? 'PROPONE_HORARIO' : 'RECHAZA_HORARIO';
    if (
      /^(SI|OK|DALE|CLARO|PERFECTO|LISTO|VA|VALE|CONFIRMO|DE ACUERDO|BUENO)\b/.test(norm) ||
      has(
        norm,
        'me parece',
        'el primero',
        'el segundo',
        'la primera',
        'la segunda',
        'esa',
        'ese',
        'confirmo',
        'de acuerdo',
        'perfecto',
      )
    )
      return 'ACEPTA_CITA';
    if (data.appointmentText) return 'PROPONE_HORARIO';
  }
  if (
    has(
      norm,
      'cita',
      'visita',
      'visitar',
      'ir a ver',
      'conocer el proyecto',
      'agendar',
      'reunion',
      'cuando puedo ir',
      'sala de ventas',
      'ver el depa',
      'ver el departamento',
      'pasar por',
    )
  )
    return data.appointmentText ? 'PROPONE_HORARIO' : 'PIDE_CITA';
  if (data.appointmentText && has(norm, 'puedo', 'podria', 'estoy libre', 'me acomoda'))
    return 'PROPONE_HORARIO';
  if (
    has(
      norm,
      'separacion',
      'inicial',
      'cuota',
      'credito',
      'financi',
      'banco',
      'hipotec',
      'mivivienda',
      'bono',
      'letras',
      'al contado',
    )
  )
    return 'PREGUNTA_FINANCIAMIENTO';
  if (
    has(
      norm,
      'precio',
      'cuesta',
      'costo',
      'valor',
      'cuanto esta',
      'cuanto sale',
      'desde cuanto',
      'tarifa',
    )
  )
    return 'PREGUNTA_PRECIO';
  if (
    has(
      norm,
      'donde queda',
      'donde esta',
      'ubicacion',
      'direccion',
      'como llego',
      'cerca de',
      'en que distrito',
      'zona',
    )
  )
    return 'PREGUNTA_UBICACION';
  if (
    has(
      norm,
      'entrega',
      'entregan',
      'avance',
      'obra',
      'construccion',
      'preventa',
      'listo para',
      'cuando estaria',
    )
  )
    return 'PREGUNTA_ENTREGA';
  if (has(norm, 'gracias', 'muchas gracias') && !text.includes('?')) return 'AGRADECE';
  if (
    text.includes('?') ||
    has(
      norm,
      'area',
      'metros',
      'm2',
      'estacionamiento',
      'deposito',
      'piscina',
      'mascota',
      'vista',
      'piso',
      'ascensor',
      'gimnasio',
      'terraza',
    )
  )
    return 'PREGUNTA_OTRA';
  if (short && has(norm, 'hola', 'buenos dias', 'buenas', 'que tal', 'buen dia')) return 'SALUDO';
  if (
    has(
      norm,
      'me interesa',
      'informacion',
      'quiero saber',
      'quisiera',
      'me gustaria',
      'info',
      'busco',
      'estoy buscando',
      'quiero',
    ) ||
    Object.values(data).some((v) => v !== undefined && v !== null)
  )
    return 'INTERES';
  return 'FUERA_DE_TEMA';
}

export function temperatureFor(intent: BotIntent, merged: BotExtracted): BotTemperature {
  if (['PIDE_CITA', 'ACEPTA_CITA', 'PROPONE_HORARIO', 'PREGUNTA_FINANCIAMIENTO'].includes(intent))
    return 'CALIENTE';
  if (merged.budget && merged.bedrooms !== undefined && merged.bedrooms !== null) return 'CALIENTE';
  if (merged.horizon === 'INMEDIATO' || merged.horizon === 'MENOS_3_MESES') return 'CALIENTE';
  if (['NO_INTERESA', 'STOP', 'FUERA_DE_TEMA', 'AGRADECE'].includes(intent)) return 'FRIO';
  if (intent.startsWith('PREGUNTA') || intent === 'INTERES' || intent === 'PIDE_HUMANO')
    return 'TIBIO';
  return Object.values(merged).some((v) => v !== undefined && v !== null) ? 'TIBIO' : 'FRIO';
}

/** Une lo nuevo con lo ya sabido (lo nuevo manda; null / undefined no borran). */
export function mergeExtracted(current: BotExtracted, next: BotExtracted): BotExtracted {
  const out: BotExtracted = { ...current };
  for (const [k, v] of Object.entries(next) as Array<[keyof BotExtracted, unknown]>) {
    if (v !== undefined && v !== null && v !== '') (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

export function describeExtracted(d: BotExtracted): string {
  const parts: string[] = [];
  if (d.name) parts.push(`se llama ${d.name}`);
  if (d.purpose) parts.push(d.purpose === 'VIVIENDA' ? 'busca para vivir' : 'busca para invertir');
  if (d.bedrooms !== undefined && d.bedrooms !== null)
    parts.push(
      d.bedrooms === 0
        ? 'quiere un estudio'
        : `quiere ${d.bedrooms} dormitorio${d.bedrooms === 1 ? '' : 's'}`,
    );
  if (d.budget)
    parts.push(`presupuesto ${d.budgetCurrency ?? 'USD'} ${d.budget.toLocaleString('es-PE')}`);
  if (d.district) parts.push(`prefiere ${d.district}`);
  if (d.financing) parts.push(`pagaría ${d.financing.toLowerCase().replace('_', ' ')}`);
  if (d.horizon) parts.push(`compra ${d.horizon.toLowerCase().replace(/_/g, ' ')}`);
  if (d.appointmentText) parts.push(`propone "${d.appointmentText}"`);
  return parts.join(' · ');
}

/** Entendimiento completo por reglas para un mensaje del cliente. */
export function understandByRules(text: string, state: BotConversationState): BotUnderstanding {
  // Plan Básico guiado: un número responde al menú que el bot ofreció en su último mensaje.
  const choice = applyMenu(text, state.menu);
  if (choice) return understandingFromChoice(choice, state);
  const data = extractData(text);
  const intent = classifyIntent(text, state, data);
  if (intent === 'ACEPTA_CITA' && !data.appointmentText)
    data.appointmentText = text.trim().slice(0, 120);
  const merged = mergeExtracted(state.data, data);
  const temperature = temperatureFor(intent, merged);
  const detail = describeExtracted(data);
  return {
    intent,
    temperature,
    data,
    summary: `${BOT_INTENT_LABELS[intent]}${detail ? ` · ${detail}` : ''}`.slice(0, 300),
    confidence: intent === 'FUERA_DE_TEMA' ? 0.4 : 0.85,
  };
}
